import { type Hue, hueFor } from '../ui/v2/hue';

/**
 * Plan vocabulary shared by Billing, Plans and Activation.
 *
 * Colour: a plan is a *category*, so it gets one stable categorical hue —
 * the same hue on the Billing "Plan" row, the plan chooser, the receipt and
 * the activation page. The hue is a small square swatch (`PlanSwatch`); it
 * is never on a button, a border or the usage bars (usage is `accent`;
 * over-limit is semantic).
 *
 * The tiers use sky → violet → rose, the hues furthest from the semantic
 * greens/ambers/reds, because a plan swatch usually sits one line away from
 * a status dot ("▪ Growth · ● Active"). Teal is the group/enterprise tier.
 *
 * Add-ons are deliberately not hued: an add-on is a checkbox with a price,
 * not a category.
 */

const PLAN_HUE: Array<[RegExp, Hue]> = [
  [/starter|basic|lite/, 'sky'],
  [/growth|standard|plus/, 'violet'],
  [/pro|scale|premium/, 'rose'],
  [/enterprise|group|multi/, 'teal'],
];

/** Stable hue for a plan code. Unknown codes hash to a hue (never accent). */
export function planHue(code: string | null | undefined): Hue {
  const c = (code ?? '').toLowerCase();
  for (const [re, hue] of PLAN_HUE) if (re.test(c)) return hue;
  return hueFor(c || 'plan');
}

export type AddonKey = 'multilingual' | 'sms_ordering' | 'opentable_resy';

export const ADDON_KEYS: AddonKey[] = [
  'multilingual',
  'sms_ordering',
  'opentable_resy',
];

export const ADDON_META: Record<
  AddonKey,
  { label: string; short: string; description: string }
> = {
  multilingual: {
    label: 'Multilingual agent',
    short: 'Multilingual',
    description: 'Arthur answers in the caller’s language — 30+ languages.',
  },
  sms_ordering: {
    label: 'SMS ordering & reservations',
    short: 'SMS',
    description: 'Guests can text to order or book; Arthur replies.',
  },
  opentable_resy: {
    label: 'OpenTable / Resy',
    short: 'OpenTable / Resy',
    description: 'Arthur books, changes and cancels real reservations.',
  },
};

/** Request-body flag the subscription and activation endpoints accept. */
export const ADDON_ACTIVE_FIELD: Record<AddonKey, string> = {
  multilingual: 'multilingual_active',
  sms_ordering: 'sms_ordering_active',
  opentable_resy: 'opentable_resy_active',
};

export interface AddonPricing {
  cents: number;
  included: boolean;
}

/**
 * Everything the money path needs to describe one plan or one custom
 * offer. Billing and Activation build it from their API shapes.
 */
export interface PlanPricing {
  currency: string;
  baseCents: number;
  includedMinutes: number;
  overageCentsPerMinute: number;
  setupFeeCents: number;
  perOrderCents: number;
  addons: Record<AddonKey, AddonPricing>;
  trialDays?: number;
}

export type AddonSelection = Record<AddonKey, boolean>;

export const NO_ADDONS: AddonSelection = {
  multilingual: false,
  sms_ordering: false,
  opentable_resy: false,
};

/** Included add-ons start selected (and stay so). */
export function includedSelection(p: PlanPricing): AddonSelection {
  return {
    multilingual: p.addons.multilingual.included,
    sms_ordering: p.addons.sms_ordering.included,
    opentable_resy: p.addons.opentable_resy.included,
  };
}

/** An add-on counts toward the monthly total only when active and not included. */
export function addonChargeCents(p: PlanPricing, sel: AddonSelection): number {
  return ADDON_KEYS.reduce((sum, k) => {
    const a = p.addons[k];
    return sum + (sel[k] && !a.included ? a.cents : 0);
  }, 0);
}

export function monthlyTotalCents(p: PlanPricing, sel: AddonSelection): number {
  return p.baseCents + addonChargeCents(p, sel);
}

/** `{ multilingual_active: …, … }` with included add-ons forced on. */
export function addonPayload(
  p: PlanPricing,
  sel: AddonSelection,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const k of ADDON_KEYS) {
    out[ADDON_ACTIVE_FIELD[k]] = p.addons[k].included || sel[k];
  }
  return out;
}

export interface AddonLine {
  key: AddonKey;
  label: string;
  cents: number;
  included: boolean;
  active: boolean;
}

export function addonLines(p: PlanPricing, sel: AddonSelection): AddonLine[] {
  return ADDON_KEYS.map((k) => ({
    key: k,
    label: ADDON_META[k].label,
    cents: p.addons[k].cents,
    included: p.addons[k].included,
    active: p.addons[k].included || sel[k],
  }));
}

/**
 * Money: whole dollars when the amount is whole, cents otherwise; `exact`
 * forces cents (invoices, proration). Intl handles the currency mark
 * (`$299`, `CA$299`).
 */
export function formatMoney(
  cents: number,
  currency = 'USD',
  opts: { exact?: boolean } = {},
): string {
  const whole = cents % 100 === 0 && !opts.exact;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(whole ? 0 : 2)} ${currency}`;
  }
}

/** Per-unit rates always show cents: `$0.12/min`, `$0.50/order`. */
export function formatRate(
  cents: number,
  currency = 'USD',
  unit: 'min' | 'order' | 'mo',
): string {
  return `${formatMoney(cents, currency, { exact: unit !== 'mo' })}/${unit}`;
}

export function formatMinutes(m: number): string {
  return `${Math.round(m).toLocaleString('en-US')} min`;
}
